import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { Gift, Layers } from 'lucide-react';
import {
  cancelPlanOverride,
  describeError,
  getPlatformPlans,
  getTenantPlan,
  grantPlanOverride,
  problemErrorCode,
  TRIAL_PLAN_CODE,
  type EntitlementSource,
  type PlanOverrideStatus,
  type PlanOverrideView,
  type PlatformPlan,
  type TenantPlanChange,
  type TenantPlanOverview,
  type TenantPlanSubscription,
} from '@/lib/api';
import { overrideEndsAtFor, overrideLastDay as lastDay, saoPauloIsoDate } from '@/lib/plan-override';
import { operatorLabel } from '@/lib/printing';
import { useAuthStore, useCanDoWriteAction } from '@/stores/auth';
import { Badge, type BadgeTone } from './ui';
import { CardTable, THead, TBody, Tr, Th, Td } from './CardTable';

/**
 * Seção "Plano" do detalhe do tenant (change billing-plans, capability
 * platform-backoffice): o plano efetivo e de onde ele vem, a assinatura, a
 * mudança de plano aberta, a cortesia vigente e o histórico de cortesias.
 *
 * A leitura (`GET .../plan`, sob `RequirePlatformCommercial`) é dos papéis
 * `Commercial` e `Admin`; para o suporte a seção não aparece. A única escrita
 * é a cortesia de plano — dar e encerrar —, que exige
 * `platform.plan_overrides.write` (2.7 da change platform-backoffice-hardening);
 * diante do token da API anterior, que não traz a permissão, as ações caem
 * para o papel (`useCanDoWriteAction`). Não há ação de mudar o plano da
 * assinatura: isso é do dono, no painel, porque mexe na cobrança.
 *
 * A cortesia dá o plano, não o acesso nem a pausa da cobrança. Por isso a
 * seção mostra a cortesia sem efeito como tal: a de plano igual ou menor que o
 * da assinatura ("Sem efeito enquanto a assinatura do {plano} valer") e a do
 * tenant sem acesso, que aponta para a concessão de acesso.
 */

const OVERRIDE_ROLES = new Set(['Commercial', 'Admin']);

const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const saoPauloDate = new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', dateStyle: 'short' });
const saoPauloDateTime = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo',
  dateStyle: 'short',
  timeStyle: 'short',
});

/** Prazo máximo da cortesia na API, a partir de agora. */
const MAX_OVERRIDE_DAYS = 366;

const REASON_MIN = 3;
const REASON_MAX = 500;

export function TenantPlanSection({ tenantId }: { tenantId: string }) {
  const role = useAuthStore((s) => s.operator?.role ?? null);
  if (!role || !OVERRIDE_ROLES.has(role)) return null;
  return <TenantPlanSectionContent tenantId={tenantId} />;
}

function TenantPlanSectionContent({ tenantId }: { tenantId: string }) {
  const currentOperatorId = useAuthStore((s) => s.operator?.id ?? null);
  const canWrite = useCanDoWriteAction('planOverrides');
  const [overview, setOverview] = useState<TenantPlanOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [notice, setNotice] = useState<{ text: string; error: boolean } | null>(null);

  const reload = useCallback(() => setReloadTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    getTenantPlan(tenantId)
      .then((data) => {
        if (cancelled) return;
        setOverview(data);
        setError(null);
      })
      .catch((err) => !cancelled && setError(describeError(err)));
    return () => {
      cancelled = true;
    };
  }, [tenantId, reloadTick]);

  return (
    <section className="card" aria-labelledby="tenant-plan-title">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <Layers size={18} />
        <div className="card-title" id="tenant-plan-title">
          Plano
        </div>
        {overview && <SourceBadge source={overview.source} />}
      </div>

      {error && !overview ? (
        <div className="error-banner" role="alert">
          {error}
        </div>
      ) : !overview ? (
        <p className="muted" style={{ margin: 0 }}>
          Carregando…
        </p>
      ) : (
        <>
          {notice && (
            <div
              className={notice.error ? 'alert alert-error' : 'alert'}
              role={notice.error ? 'alert' : 'status'}
              style={{ marginBottom: 12 }}
            >
              {notice.text}
            </div>
          )}
          <EffectivePlan overview={overview} tenantId={tenantId} />
          <ActiveOverride
            overview={overview}
            tenantId={tenantId}
            currentOperatorId={currentOperatorId}
            canWrite={canWrite}
            onCancelled={() => {
              setNotice({
                text: 'Cortesia encerrada. O plano efetivo voltou a seguir a assinatura ou o teste grátis.',
                error: false,
              });
              reload();
            }}
          />
          {!canWrite ? null : overview.activeOverride ? (
            // Uma cortesia vigente por tenant: a API recusa a segunda com 409.
            <p className="muted" style={{ margin: '0 0 12px', fontSize: 13 }} data-testid="grant-blocked">
              Para dar outra cortesia de plano, encerre a vigente antes.
            </p>
          ) : (
            <GrantOverride
              tenantId={tenantId}
              onGranted={(message) => {
                setNotice({ text: message, error: false });
                reload();
              }}
              onConflict={() => {
                // Outra aba ou outro operador deu a cortesia antes: a releitura mostra a vigente.
                setNotice({
                  text: 'Esta loja já tem uma cortesia vigente. Encerre a vigente antes de dar outra.',
                  error: true,
                });
                reload();
              }}
            />
          )}
          <OverrideHistory overrides={overview.overrides} currentOperatorId={currentOperatorId} />
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
//  Rótulos
// ---------------------------------------------------------------------------

function sourceLabel(source: EntitlementSource): { label: string; tone: BadgeTone } {
  switch (source) {
    case 'override':
      return { label: 'Cortesia', tone: 'primary' };
    case 'subscription':
      return { label: 'Assinatura', tone: 'success' };
    case 'trial':
      return { label: 'Teste grátis', tone: 'info' };
    case 'none':
      return { label: 'Sem acesso', tone: 'error' };
  }
}

function SourceBadge({ source }: { source: EntitlementSource }) {
  const { label, tone } = sourceLabel(source);
  return (
    <Badge tone={tone} dot>
      Fonte: {label}
    </Badge>
  );
}

function subscriptionStatusLabel(status: string): string {
  switch (status) {
    case 'pending':
      return 'Pendente (nunca paga)';
    case 'active':
      return 'Ativa';
    case 'overdue':
      return 'Em atraso';
    case 'cancelled':
      return 'Cancelada';
    default:
      return status;
  }
}

function overrideStatusLabel(status: PlanOverrideStatus): { label: string; tone: BadgeTone } {
  switch (status) {
    case 'active':
      return { label: 'Vigente', tone: 'success' };
    case 'ended':
      return { label: 'Terminou', tone: 'neutral' };
    case 'cancelled':
      return { label: 'Encerrada', tone: 'warning' };
  }
}

function changeLabel(change: TenantPlanChange): string {
  const target = change.toPlanName ?? change.toPlanCode ?? 'outro plano';
  const upgrade = change.kind === 'upgrade';
  switch (change.status) {
    case 'awaiting_payment':
      return (
        `Upgrade para ${target}: aguardando o pagamento de ${brl.format(change.prorationAmount)}` +
        (change.paymentDueDate ? `, que vence em ${formatIsoDate(change.paymentDueDate)}` : '')
      );
    case 'scheduled':
      return `Downgrade para ${target}: agendado para ${formatDate(change.effectiveAt)}`;
    case 'applying':
    case 'failed_retryable':
      return upgrade
        ? `Upgrade para ${target} já pago e valendo desde ${formatDateTime(change.effectiveAt)}; atualizando a cobrança recorrente`
        : `Downgrade para ${target}: agendamento em processamento`;
    default:
      return `${upgrade ? 'Upgrade' : 'Downgrade'} para ${target}`;
  }
}

function formatDate(iso: string | null | undefined): string {
  return iso ? saoPauloDate.format(new Date(iso)) : '—';
}

function formatDateTime(iso: string | null | undefined): string {
  return iso ? saoPauloDateTime.format(new Date(iso)) : '—';
}

/** "2026-10-14" → "14/10/2026", sem passar por fuso. */
function formatIsoDate(date: string | null | undefined): string {
  if (!date) return '—';
  const [y, m, d] = date.split('-');
  return y && m && d ? `${d}/${m}/${y}` : date;
}

// ---------------------------------------------------------------------------
//  Plano efetivo, assinatura e mudança aberta
// ---------------------------------------------------------------------------

function EffectivePlan({ overview, tenantId }: { overview: TenantPlanOverview; tenantId: string }) {
  const { subscription, pendingPlanChange } = overview;
  return (
    <div className="detail-grid" style={{ marginBottom: 12 }}>
      <div className="k">Plano efetivo</div>
      <div>
        <strong data-testid="effective-plan">{overview.source === 'none' ? 'Nenhum' : (overview.planName ?? overview.planCode)}</strong>{' '}
        <span className="muted">({sourceLabel(overview.source).label.toLowerCase()})</span>
        {overview.source === 'override' && overview.overrideEndsAt && (
          <span className="muted"> · cortesia até {lastDay(overview.overrideEndsAt)}</span>
        )}
        {overview.source === 'none' && (
          <div className="muted" style={{ fontSize: 13 }}>
            A loja está sem acesso ao painel (teste vencido, assinatura encerrada ou loja desativada): nenhum plano
            vale. Para liberar o acesso, use uma{' '}
            <Link to={`/tenants/${tenantId}/concessions`}>concessão de acesso</Link>.
          </div>
        )}
      </div>

      <div className="k">Assinatura</div>
      <div>{subscription ? <SubscriptionSummary subscription={subscription} /> : <span className="muted">Sem assinatura</span>}</div>

      <div className="k">Mudança de plano</div>
      <div data-testid="pending-plan-change">
        {pendingPlanChange ? changeLabel(pendingPlanChange) : <span className="muted">Nenhuma em andamento</span>}
      </div>
    </div>
  );
}

function SubscriptionSummary({ subscription }: { subscription: TenantPlanSubscription }) {
  const cycle = subscription.billingCycle === 'yearly' ? 'anual' : 'mensal';
  const discounted = subscription.value !== subscription.fullValue;
  return (
    <span data-testid="subscription-summary">
      {subscription.planName ?? subscription.planCode ?? 'Plano desconhecido'} · {cycle} ·{' '}
      {subscriptionStatusLabel(subscription.status)} · {brl.format(subscription.value)}
      {discounted && <span className="muted"> (cheio {brl.format(subscription.fullValue)})</span>}
      {subscription.nextDueDate && <span className="muted"> · pago até {formatIsoDate(subscription.nextDueDate)}</span>}
      {subscription.accessEndsAt && <span className="muted"> · acesso até {formatDateTime(subscription.accessEndsAt)}</span>}
    </span>
  );
}

// ---------------------------------------------------------------------------
//  Cortesia vigente e encerramento
// ---------------------------------------------------------------------------

function ActiveOverride({
  overview,
  tenantId,
  currentOperatorId,
  canWrite,
  onCancelled,
}: {
  overview: TenantPlanOverview;
  tenantId: string;
  currentOperatorId: string | null;
  canWrite: boolean;
  onCancelled: () => void;
}) {
  const [closing, setClosing] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const active = overview.activeOverride;
  if (!active) return null;

  const trimmed = reason.trim();
  const reasonValid = trimmed.length >= REASON_MIN && trimmed.length <= REASON_MAX;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!reasonValid) return;
    setSubmitting(true);
    setError(null);
    try {
      await cancelPlanOverride(tenantId, trimmed);
      setClosing(false);
      setReason('');
      onCancelled();
    } catch (err) {
      setError(describeError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="alert" style={{ marginBottom: 12 }} data-testid="active-override">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <Gift size={16} />
        <strong>Cortesia vigente: {active.planName}</strong>
        <span className="muted">até {lastDay(active.endsAt)}</span>
      </div>
      <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
        Motivo: {active.reason} · dada por {operatorLabel(active.createdByOperatorId, currentOperatorId)} em{' '}
        {formatDateTime(active.startsAt)}
      </div>
      {!overview.activeOverrideInEffect && <IneffectiveOverrideNote overview={overview} tenantId={tenantId} />}

      {!canWrite ? null : !closing ? (
        <div style={{ marginTop: 8 }}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setClosing(true)}>
            Encerrar cortesia
          </button>
        </div>
      ) : (
        <form onSubmit={submit} aria-label="Encerrar cortesia" style={{ marginTop: 8 }}>
          <div className="field">
            <label htmlFor="plan-override-cancel-reason">Motivo do encerramento</label>
            <textarea
              id="plan-override-cancel-reason"
              rows={2}
              maxLength={REASON_MAX}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
          {error && (
            <div className="alert alert-error" role="alert">
              {error}
            </div>
          )}
          <div className="btn-row">
            <button type="submit" className="btn btn-sm" disabled={!reasonValid || submitting}>
              Confirmar encerramento
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={submitting}
              onClick={() => {
                setClosing(false);
                setError(null);
              }}
            >
              Voltar
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function IneffectiveOverrideNote({ overview, tenantId }: { overview: TenantPlanOverview; tenantId: string }) {
  if (overview.source === 'none') {
    return (
      <p style={{ margin: '6px 0 0', fontSize: 13 }} data-testid="override-without-effect">
        <strong>Sem efeito:</strong> a cortesia dá o plano, mas não dá acesso, e esta loja está sem acesso ao painel.
        Para liberar, use uma <Link to={`/tenants/${tenantId}/concessions`}>concessão de acesso</Link>.
      </p>
    );
  }
  if (overview.source === 'subscription') {
    const plan = overview.subscription?.planName ?? overview.planName ?? 'plano pago';
    return (
      <p style={{ margin: '6px 0 0', fontSize: 13 }} data-testid="override-without-effect">
        <strong>Sem efeito enquanto a assinatura do {plan} valer.</strong> A cortesia só vale quando o plano dela é maior
        que o da assinatura.
      </p>
    );
  }
  return (
    <p style={{ margin: '6px 0 0', fontSize: 13 }} data-testid="override-without-effect">
      <strong>Sem efeito no momento.</strong>
    </p>
  );
}

// ---------------------------------------------------------------------------
//  Dar cortesia
// ---------------------------------------------------------------------------

function GrantOverride({
  tenantId,
  onGranted,
  onConflict,
}: {
  tenantId: string;
  onGranted: (message: string) => void;
  onConflict: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [plans, setPlans] = useState<PlatformPlan[] | null>(null);
  const [plansError, setPlansError] = useState<string | null>(null);
  const [planCode, setPlanCode] = useState('');
  const [until, setUntil] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || plans) return;
    let cancelled = false;
    getPlatformPlans()
      .then((data) => {
        if (cancelled) return;
        if (data === null) setPlansError('Esta versão da API não tem o catálogo de planos.');
        else setPlans(data);
      })
      .catch((err) => !cancelled && setPlansError(describeError(err)));
    return () => {
      cancelled = true;
    };
  }, [open, plans]);

  // O teste grátis e os arquivados não podem ser dados; o fora da venda pode (é o piloto).
  const options = useMemo(
    () => (plans ?? []).filter((p) => p.code !== TRIAL_PLAN_CODE && !p.archivedAt).sort((a, b) => a.sortOrder - b.sortOrder),
    [plans],
  );

  const minDay = saoPauloIsoDate(0);
  const maxDay = saoPauloIsoDate(MAX_OVERRIDE_DAYS - 1);
  const trimmed = reason.trim();
  const valid =
    planCode !== '' &&
    until !== '' &&
    until >= minDay &&
    until <= maxDay &&
    trimmed.length >= REASON_MIN &&
    trimmed.length <= REASON_MAX;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!valid) return;
    setSubmitting(true);
    setError(null);
    try {
      const granted = await grantPlanOverride(tenantId, {
        planCode,
        endsAt: overrideEndsAtFor(until),
        reason: trimmed,
      });
      setOpen(false);
      setPlanCode('');
      setUntil('');
      setReason('');
      onGranted(
        granted.effectiveSource === 'override'
          ? `Cortesia de ${granted.planName} dada até ${lastDay(granted.endsAt)}.`
          : `Cortesia de ${granted.planName} registrada, mas sem efeito agora: o plano efetivo não mudou.`,
      );
    } catch (err) {
      if (problemErrorCode(err) === 'billing.plan_override_active') {
        onConflict();
      } else {
        setError(describeError(err));
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) {
    return (
      <div style={{ marginBottom: 12 }}>
        <button type="button" className="btn btn-sm" onClick={() => setOpen(true)}>
          <Gift size={15} /> Dar cortesia de plano
        </button>
        {error && (
          <div className="alert alert-error" role="alert" style={{ marginTop: 8 }}>
            {error}
          </div>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} aria-label="Dar cortesia de plano" className="card" style={{ marginBottom: 12 }}>
      <div className="card-title" style={{ marginBottom: 8 }}>
        Dar cortesia de plano
      </div>
      <p className="muted" style={{ marginTop: 0, fontSize: 13 }} data-testid="override-warning">
        A cortesia dá o plano até o dia escolhido, inclusive. Ela <strong>não dá acesso</strong> ao painel (para isso,
        use uma concessão de acesso) e <strong>não interrompe a cobrança</strong> da assinatura.
      </p>
      {plansError && (
        <div className="alert alert-error" role="alert">
          {plansError}
        </div>
      )}
      <div className="grid-2">
        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor="plan-override-plan">Plano</label>
          <select id="plan-override-plan" value={planCode} onChange={(e) => setPlanCode(e.target.value)}>
            <option value="">Escolha o plano</option>
            {options.map((p) => (
              <option key={p.code} value={p.code}>
                {p.name}
                {p.isPublic ? '' : ' (fora da venda)'}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor="plan-override-until">Vale até (inclusive)</label>
          <input
            id="plan-override-until"
            type="date"
            min={minDay}
            max={maxDay}
            value={until}
            onChange={(e) => setUntil(e.target.value)}
          />
        </div>
        <div className="field" style={{ marginBottom: 0, gridColumn: '1 / -1' }}>
          <label htmlFor="plan-override-reason">Motivo</label>
          <textarea
            id="plan-override-reason"
            rows={2}
            maxLength={REASON_MAX}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="ex.: piloto do atendente"
          />
        </div>
      </div>
      {error && (
        <div className="alert alert-error" role="alert" style={{ marginTop: 8 }}>
          {error}
        </div>
      )}
      <div className="btn-row" style={{ marginTop: 12 }}>
        <button type="submit" className="btn btn-sm" disabled={!valid || submitting}>
          Dar cortesia
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          disabled={submitting}
          onClick={() => {
            setOpen(false);
            setError(null);
          }}
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
//  Histórico
// ---------------------------------------------------------------------------

function OverrideHistory({
  overrides,
  currentOperatorId,
}: {
  overrides: PlanOverrideView[];
  currentOperatorId: string | null;
}) {
  return (
    <div>
      <div style={{ fontWeight: 600, fontSize: 14, margin: '4px 0 8px' }}>Histórico de cortesias</div>
      {overrides.length === 0 ? (
        <p className="muted" style={{ margin: 0, fontSize: 14 }}>
          Nenhuma cortesia de plano dada a esta loja.
        </p>
      ) : (
        <div className="table-scroll">
          <CardTable aria-label="Histórico de cortesias">
            <THead>
              <Tr>
                <Th>Plano</Th>
                <Th>Período</Th>
                <Th>Situação</Th>
                <Th>Motivo</Th>
                <Th>Dada por</Th>
                <Th>Encerramento</Th>
              </Tr>
            </THead>
            <TBody>
              {overrides.map((o) => {
                const status = overrideStatusLabel(o.status);
                return (
                  <Tr key={o.id}>
                    <Td heading>{o.planName}</Td>
                    <Td label="Período">
                      {formatDate(o.startsAt)} a {lastDay(o.endsAt)}
                    </Td>
                    <Td label="Situação">
                      <Badge tone={status.tone}>{status.label}</Badge>
                    </Td>
                    <Td label="Motivo">{o.reason}</Td>
                    <Td label="Dada por">{operatorLabel(o.createdByOperatorId, currentOperatorId)}</Td>
                    <Td label="Encerramento">
                      {o.cancelledAt ? (
                        <>
                          {formatDateTime(o.cancelledAt)} por {operatorLabel(o.cancelledByOperatorId, currentOperatorId)}
                          {o.cancellationReason && <div className="muted" style={{ fontSize: 12 }}>{o.cancellationReason}</div>}
                        </>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </TBody>
          </CardTable>
        </div>
      )}
    </div>
  );
}
